import cn from 'classnames'
import qs from 'qs'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDisclosure } from '@nextui-org/react'
import MainButton from '../common/util/input/MainButton'
import get from '../helpers/Api'
import {
    getEndpoint,
    getOwnerTerm,
    setQueryStringWithoutPageReloadPush,
} from '../helpers/FormatLinks'
import { ScrollContext } from '../page/Page'
import { SearchResults } from './SearchResults'
import TopFilters from './topfilters/TopFilters'
import DrawerLeft from './DrawerLeft'
import { useSharedState } from '../waxplorer/Waxplorer'

const SearchComponent = (props) => {
    const { isOpen, onOpen, onOpenChange } = useDisclosure()

    const searchSettings = props['searchSettings']
        ? props['searchSettings']
        : { minimal: false }

    const hideFilters = props['hideFilters']

    const [collectionSchemas, setCollectionSchemas] = useState(null)
    const [collectionAttributes, setCollectionAttributes] = useState(null)

    let values = qs.parse(
        window.location.search.substring(1, window.location.search.length),
    )

    const tradeSearch = props['trade']
    const page = props['page']

    const { t } = useTranslation('common')

    const [show, setShow] = useState(true)

    const { scrollManager, setScrollManager } = useContext(ScrollContext)
    const [searchResults, setSearchResults] = useState([])

    const [searchDate, setSearchDate] = useState(new Date())
    const [cleared, setCleared] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [offset, setOffset] = useState(0)

    const tab = values['tab']
    const [state, dispatch] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const getValue = (key, def) => {
        if (searchSettings[key] && searchSettings[key]['fixed'])
            return searchSettings[key]['value']
        if (values[key]) return unescape(values[key])
        if (searchSettings[key]) return searchSettings[key]['value']
        return def
    }

    const getOrder = (search, def) => {
        let order = def

        if (
            ['assets', 'duplicates', 'highest_duplicates', 'packs'].includes(
                search,
            )
        ) {
            order = 'date_desc'
        } else if (
            ['inventory', 'bulk_sell', 'bulk_transfer'].includes(search)
        ) {
            order = 'transferred_desc'
        } else if (search === 'pfps') {
            order = 'rarity_score_desc'
        } else if (search === 'trades') {
            order = 'date_desc'
        } else if (search === 'templates') {
            order = 'template_id_asc'
        } else if (
            ['missing', 'cheapest', 'cheapest_missing'].includes(search)
        ) {
            order = 'price_asc'
        } else if ('listings' === search) {
            order = 'date_desc'
        } else if ('auctions' === search) {
            order = 'end_asc'
        } else if (search === 'bundles') {
            order = 'date_desc'
        } else if (search === 'bulk_multi_sell') {
            order = 'owned_desc'
        } else if (
            [
                'lowest_mints',
                'highest_mints',
                'bulk_transfer_lowest_mints',
            ].includes(search)
        ) {
            order = 'mint_asc'
        } else if (
            [
                'bulk_transfer',
                'bulk_distribute',
                'bulk_cancel',
                'bulk_transfer_duplicates',
                'bulk_stake',
                'bulk_unstake',
                'bulk_sell',
                'bulk_burn',
                'bulk_sell_duplicates',
                'bulk_sell_duplicates',
                'bulk_bundle',
                'bulk_bundle_lowest_mints',
                'bulk_bundle_highest_mints',
                'bulk_bundle_highest_duplicates',
                'bulk_sell_highest_duplicates',
            ].includes(search)
        ) {
            order = 'date_desc'
        } else if (search === 'bulk_buy') {
            order = 'price_asc'
        } else if (
            search === 'buyoffers_owned' ||
            search === 'best_buyoffers_owned'
        ) {
            order = 'price_desc'
        }

        return order
    }

    const getAuthor = () => {
        return getValue('collection', null)
            ? getValue('collection', null)
            : props['collection'] && props['collection'] !== '*'
            ? props['collection']
            : '*'
    }

    const [searchVars, setSearchVars] = useState({
        searchTerm: getValue(
            'term',
            getValue(
                'name',
                getValue(
                    'template_id',
                    getValue('asset_id', getValue('number', null)),
                ),
            ),
        ),
        searchOwner: getValue('owner', null),
        searchCategory: getValue(
            'category',
            searchSettings['category']
                ? searchSettings['category']['value']
                : null,
        ),
        searchSchema: !tradeSearch
            ? getValue(
                  'schema',
                  searchSettings['schema']
                      ? searchSettings['schema']['value']
                      : null,
              )
            : null,
        searchSearch: getValue(
            'search_type',
            props['bulk'] ? 'bulk_sell' : 'listings',
        ),

        orderBy: getValue(
            'order_by',
            getOrder(
                getValue(
                    'search_type',
                    props['bulk']
                        ? 'bulk_sell'
                        : process.env.NEXT_PUBLIC_DB_ASSETS === 'TRUE'
                        ? 'listings'
                        : 'sales',
                ),
                'price_asc',
            ),
        ),
        searchContract: getValue('contract', getValue('contract', 'all')),
        searchRecentlySold: values['recent'],
        searchFavorites: values['favorites']
            ? values['favorites'] === 'true'
            : false,
        searchBacked: values['backed'] ? values['backed'] === 'true' : false,
        exactSearch: getValue(
            'exact_search',
            searchSettings['exact_search']
                ? searchSettings['exact_search']['value']
                : false,
        ),
        searchLimit: getValue(
            'limit',
            searchSettings['limit'] ? searchSettings['limit']['value'] : 40,
        ),
        searchOffset: getValue('offset', 0),
        minPrice: values['min_price'] ? values['min_price'] : 0,
        maxPrice: values['max_price'] ? values['max_price'] : 0,
        minAverage: values['min_average'] ? values['min_average'] : 0,
        maxAverage: values['max_average'] ? values['max_average'] : 0,
        minMint: values['min_mint'] ? values['min_mint'] : 0,
        maxMint: values['max_mint'] ? values['max_mint'] : 0,
        searchCollection: getAuthor(),
        searchAttributes: getValue('attributes', []),
        searchPFPs: getValue('pfps_only', false),
        searchUnlisted: getValue('unlisted_only', false),
        searchVerified: getValue(
            'verified',
            (getAuthor() && getAuthor() !== '*') || getValue('owner', null)
                ? 'all'
                : process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                ? 'all'
                : 'verified',
        ),
        includeTubes: !(
            values['include_tubes'] && values['include_tubes'] === 'false'
        ),
        searchCount: 0,
    })

    const getTerms = (search) => {
        let ownerTerm = t('search.owner')
        let searchTerm = t('search.assets')
        switch (search) {
            case 'trades':
                searchTerm = t('search.trades')
                ownerTerm = t('search.buyer')
                break
            case 'sales':
                searchTerm = t('search.sales')
                ownerTerm = t('search.seller') + '/' + t('search.market')
                break
            case 'listings':
                searchTerm = t('search.sales')
                ownerTerm = t('search.seller') + '/' + t('search.market')
                break
            case 'buyoffers':
            case 'best_buyoffers':
            case 'buyoffers_owned':
            case 'best_buyoffers_owned':
                searchTerm = t('search.sales')
                ownerTerm = t('search.buyer')
                break
            case 'auctions':
                searchTerm = t('search.auctions')
                ownerTerm = t('search.seller') + '/' + t('search.market')
                break
            case 'duplicates':
                searchTerm = t('search.duplicates')
                break
            case 'highest_duplicates':
                searchTerm = t('search.highest_duplicates')
                break
            case 'missing':
                searchTerm = t('search.missing_assets')
                break
            case 'cheapest_missing':
                searchTerm = t('search.cheapest_missing_assets')
                break
            case 'bundles':
                searchTerm = t('search.bundles')
                ownerTerm = t('search.seller') + '/' + t('search.market')
                break
        }

        return [searchTerm, ownerTerm]
    }

    const [searchTerm, ownerTerm] = getTerms(searchVars.searchSearch)

    const currentSearchPath = []
    const onAdd = props['onAdd']
    const displayCollectionSelector =
        props['displayCollectionSelector'] !== undefined
            ? props['displayCollectionSelector']
            : !searchSettings ||
              !searchSettings['collection'] ||
              !searchSettings['collection']['fixed']

    const [cache, setCache] = useState(null) //useStorage(`last-search-results`)

    const [hasCache, setHasCahe] = useState(false)

    const lastSearchResult = hasCache && cache && cache.lastSearchResult

    const lastSearchPath = hasCache && cache && cache.lastSearchPath

    const [searchPathCounter, setSearchPathCounter] = useState(false)

    const targetTab = searchSettings ? searchSettings['tab'] : null
    const bulk = props['bulk']

    const initScrollManager = async (
        triggered,
        useManager = true,
        scrollUp = false,
    ) => {
        if (searchVars.searchSearch.includes('bulk_') || tradeSearch) {
            setScrollManager({
                useManager: false,
                offset: 0,
                triggered: triggered,
                scrollUp: scrollUp,
            })
        } else {
            setScrollManager({
                useManager: useManager,
                offset: 0,
                triggered: triggered,
                scrollUp: scrollUp,
            })
        }
    }

    useEffect(() => {
        const asyncFunc = async () => {
            if (scrollManager.triggered) {
                loadMore()
            }
        }
        asyncFunc()
    }, [scrollManager.triggered])

    useEffect(() => {
        const asyncFunc = async () => {
            if (offset > 0) {
                if (isLoading && (!targetTab || !tab || targetTab === tab)) {
                    if (!bulk || userName) {
                        runSearch(
                            {
                                offset: offset,
                                owner:
                                    bulk &&
                                    userName &&
                                    searchVars.searchSearch !== 'bulk_buy'
                                        ? userName
                                        : searchVars.searchOwner,
                            },
                            false,
                        )
                    }
                }
            }
        }
        asyncFunc()
    }, [offset])

    const asyncFunc = async () => {
        if (
            searchVars.searchLimit ===
                (searchSettings['limit']
                    ? searchSettings['limit']['value']
                    : 40) &&
            searchVars.searchSearch &&
            searchVars.searchSearch.includes('bulk_')
        ) {
            dispatch({ type: 'SET_ASSETS', payload: [] })
        }
        if (!isLoading && (!targetTab || !tab || targetTab === tab)) {
            if (!bulk || userName) {
                if (offset > 0) {
                    try {
                        runSearch(
                            {
                                offset: offset,
                                owner:
                                    bulk &&
                                    userName &&
                                    searchVars.searchSearch !== 'bulk_buy'
                                        ? userName
                                        : searchVars.searchOwner,
                            },
                            false,
                        )
                    } catch (e) {
                        console.error(e)
                    }
                } else {
                    runSearch(
                        {
                            offset: offset,
                            owner:
                                bulk &&
                                userName &&
                                searchVars.searchSearch !== 'bulk_buy'
                                    ? userName
                                    : searchVars.searchOwner,
                        },
                        true,
                        true,
                    )
                }
            }
        }
    }

    useEffect(() => {
        ;(async () => asyncFunc())()
    }, [searchVars.searchCount, tab]) // Add dependencies here

    const isPackSearch =
        searchVars.searchSearch === 'ft_packs' ||
        searchVars.searchSearch === 'my_ft_packs'

    const getCollectionSchemas = async (collection) => {
        if (collection && collection !== '*') {
            const path = `collection-schemas/${collection}`

            const result = await get(path, 'wax-api')
            setCollectionSchemas(result)
        }
    }

    const getCollectionAttributes = async (collection, schema) => {
        if (collection && collection !== '*') {
            const result = await get(
                `filter-attributes-simple/${collection}${
                    schema ? '?schema=' + schema : ''
                }`,
                'wax-api',
            )

            setCollectionAttributes(result)
        }
    }

    useEffect(() => {
        const asyncFunc = async () => {
            if (!scrollManager || !scrollManager.useManager) {
                initScrollManager(false, true, false)
            }
        }
        asyncFunc()
    }, [isLoading])

    const parseSearchResult = async (res) => {
        setCache({
            lastSearchResult: res['result'],
            lastSearchPath: res['searchPath'],
        })
        setHasCahe(true)
        setSearchResults(res['result'])

        setIsLoading(false)

        initScrollManager(false)
    }

    const checkLastSearch = (searchPath, initial) => {
        if (lastSearchPath === searchPath && initial && tab !== 'inventory') {
            setSearchResults(lastSearchResult)
            setIsLoading(false)
        } else {
            get(searchPath, 'wax-api').then((result) =>
                getSearchResult(result, searchPath).then(parseSearchResult),
            )
        }
    }

    const runSearch = async (newVars, initial = false) => {
        try {
            let {
                term,
                owner,
                collection,
                schema,
                category,
                order,
                search,
                offer_type,
                min_price,
                max_price,
                min_mint,
                max_mint,
                contract,
                verified,
                limit,
                recent,
                attributes,
                pfps_only,
            } = getNewVars(newVars)

            if (
                collection &&
                collection !== '*' &&
                (!collectionSchemas ||
                    !Object.keys(collectionSchemas).includes('schemas') ||
                    collectionSchemas['schemas'].length === 0 ||
                    collectionSchemas['schemas'][0].collection !== collection)
            ) {
                getCollectionSchemas(collection)
            }

            if (
                collection &&
                schema &&
                process.env.NEXT_PUBLIC_TESTNET !== 'TRUE'
            ) {
                getCollectionAttributes(collection, schema)
            }

            if (
                [
                    'bulk_bundle',
                    'bulk_bundle_lowest_mints',
                    'bulk_bundle_highest_mints',
                    'bulk_bundle_highest_duplicates',
                ].includes(search) &&
                !collection
            )
                return

            if (!collection || collection === '*') schema = ''

            setIsLoading(true)

            if (max_price && parseFloat(max_price) < parseFloat(min_price)) {
                max_price = min_price
            }

            let result = []

            if (
                (limit !== searchSettings['limit']['value'] &&
                    search.includes('bulk_')) ||
                offset === 0
            )
                setSearchResults(result)

            const endpoint = getEndpoint(search, 'assets')

            const ownerTerm = getOwnerTerm(
                ['sells', 'buys'].includes(search) ? search : endpoint,
            )

            const searchPath = `${endpoint}?term=${
                term
                    ? term.includes('#') ||
                      term.includes('&') ||
                      term.includes('?')
                        ? escape(term)
                        : term
                    : ''
            }&${ownerTerm}=${owner ? owner : ''}&collection=${
                collection ? collection : ''
            }&schema=${schema ? schema : ''}&category=${
                category ? category : ''
            }&recent=${recent ? recent : ''}&order_by=${
                order ? order : 'asset_id'
            }&limit=${limit}&search_type=${search}&offer_type=${offer_type}&min_mint=${min_mint}&max_mint=${max_mint}&min_price=${min_price}&max_price=${max_price}&offset=${offset}&verified=${verified}&user=${
                userName ? userName : ''
            }&contract=${
                contract && contract !== 'all' ? contract : ''
            }&attributes=${attributes}&only=${pfps_only ? 'pfps' : ''}`

            currentSearchPath.push(searchPath)

            checkLastSearch(searchPath, initial)

            setSearchDate(new Date())
        } catch (e) {
            throw e
        }
    }

    const selectedAssets = props['selectedAssets']

    const searchOfferType = searchSettings['searchOfferType']
        ? searchSettings['searchOfferType']
        : 'listings'

    const getSearchResult = async (result, searchPath) => {
        if (!result || result['error']) {
            return { result: [], searchPath: searchPath }
        }

        if (offset === 0) {
            return { result: result, searchPath: searchPath }
        } else {
            return {
                result: searchResults.concat(result),
                searchPath: searchPath,
            }
        }
    }

    const getClearedVars = () => {
        return {
            term: '',
            owner:
                searchSettings &&
                searchSettings['owner'] &&
                searchSettings['owner']['fixed']
                    ? searchSettings['owner']['value']
                    : '',
            collection:
                searchSettings &&
                searchSettings['collection'] &&
                searchSettings['collection']['fixed']
                    ? searchSettings['collection']['value']
                    : '*',
            category: '',
            schema: '',
            exact: false,
            min_price: 0,
            max_price: 0,
            min_mint: 0,
            max_mint: 0,
            contract: '',
            verified: true,
            recent: '',
            favorites: false,
            offset: 0,
        }
    }

    const getNewVars = (newVars) => {
        const keys = Object.keys(newVars)

        if (!keys.includes('term')) {
            newVars['term'] = searchVars.searchTerm?.toString()
        }
        if (!keys.includes('owner')) {
            newVars['owner'] = searchVars.searchOwner
        }
        if (!keys.includes('collection')) {
            newVars['collection'] = searchVars.searchCollection
        }
        if (!keys.includes('schema')) {
            newVars['schema'] = searchVars.searchSchema
        }
        if (!keys.includes('category')) {
            newVars['category'] = searchVars.searchCategory
        }
        if (!keys.includes('exact')) {
            newVars['exact'] = searchVars.exactSearch
        }
        if (!keys.includes('order')) {
            newVars['order'] = searchVars.orderBy
        }
        if (!keys.includes('search')) {
            newVars['search'] = searchVars.searchSearch
        }
        if (!keys.includes('offer_type')) {
            newVars['offer_type'] = searchOfferType
        }
        if (!keys.includes('min_price')) {
            newVars['min_price'] = searchVars.minPrice
        }
        if (!keys.includes('max_price')) {
            newVars['max_price'] = searchVars.maxPrice
        }
        if (!keys.includes('min_mint')) {
            newVars['min_mint'] = searchVars.minMint
        }
        if (!keys.includes('max_mint')) {
            newVars['max_mint'] = searchVars.maxMint
        }
        if (!keys.includes('verified')) {
            newVars['verified'] = searchVars.searchVerified
        }
        if (!keys.includes('include_tubes')) {
            newVars['include_tubes'] = searchVars.includeTubes
        }
        if (!keys.includes('favorites')) {
            newVars['favorites'] = searchVars.searchFavorites
        }
        if (!keys.includes('backed')) {
            newVars['backed'] = searchVars.searchBacked
        }
        if (!keys.includes('limit')) {
            newVars['limit'] = searchVars.searchLimit
        }
        if (!keys.includes('offset')) {
            newVars['offset'] = offset
        }
        if (!keys.includes('recent')) {
            newVars['recent'] = searchVars.searchRecentlySold
        }
        if (!keys.includes('contract')) {
            newVars['contract'] = searchVars.searchContract
        }
        if (!keys.includes('attributes')) {
            newVars['attributes'] = searchVars.searchAttributes
        }
        if (!keys.includes('pfps_only')) {
            newVars['pfps_only'] = searchVars.searchPFPs
        }
        if (!keys.includes('unlisted_only')) {
            newVars['unlisted_only'] = searchVars.searchUnlisted
        }

        return newVars
    }

    const setVars = (newVars) => {
        let {
            term,
            owner,
            collection,
            schema,
            category,
            order,
            search,
            offer_type,
            min_price,
            max_price,
            verified,
            favorites,
            limit,
            offset,
            contract,
            recent,
            attributes,
            pfps_only,
            unlisted_only,
            exact,
            max_mint,
            min_mint,
            backed,
        } = getNewVars(newVars)

        exact = exact === 'true' || exact === true
        if (!searchVars.searchCollection || searchVars.searchCollection === '*')
            schema = ''

        if (max_mint && parseInt(max_mint) < parseInt(min_mint)) {
            max_mint = min_mint
        }

        if (
            ![
                'sales',
                'listings',
                'bundles',
                'cheapest_missing',
                'missing',
                'packs',
                'ft_packs',
                'cheapest',
                'bulk_buy',
                'owned_lower_mints',
            ].includes(search)
        ) {
            offer_type = ''
            if (search !== 'trades') {
                min_price = 0
                max_price = 0
            }
        }

        const ownerSearches = [
            'missing',
            'cheapest_missing',
            'duplicates',
            'highest_duplicates',
            'favorites',
            'my_packs',
            'owned_lower_mints',
        ]

        const possibleOwnerSearches = ['staked', 'assets', 'tubed', 'inventory']

        if (ownerSearches.includes(search) && !owner) {
            if (userName) owner = userName
            else search = 'assets'
        } else if (
            ownerSearches.includes(searchVars.searchSearch) &&
            !ownerSearches.includes(search) &&
            owner &&
            !possibleOwnerSearches.includes(search)
        ) {
            owner = ''
        }

        if (order === 'diff') {
            if (search === 'assets') {
                order = 'asset_id_asc'
            }
            if (search === 'inventory') {
                order = 'asset_id_asc'
            }
            if (search === 'templates') {
                order = 'template_id_asc'
            }
            if (search === 'trades') {
                order = 'date_desc'
            }
            if (search === 'bundles') {
                order = 'price_asc'
            }
        }

        const query = values

        delete query['name']
        delete query['number']
        delete query['asset_id']
        delete query['template_id']

        if (query['refresh']) delete query['refresh']
        if (term) {
            query['term'] = escape(term)
        } else delete query['term']
        if (
            owner &&
            tab !== 'new-trade' &&
            (!searchSettings['owner'] ||
                searchSettings['owner']['value'] !== owner)
        )
            query['owner'] = owner
        else delete query['owner']
        if (
            collection &&
            (!searchSettings['collection'] ||
                searchSettings['collection']['value'] !== collection)
        )
            query['collection'] = collection
        else delete query['collection']
        if (schema) query['schema'] = schema
        else delete query['schema']
        if (category) query['category'] = category
        else delete query['category']
        if (
            order &&
            (!searchSettings['order_by'] ||
                searchSettings['order_by']['value'] !== order)
        )
            query['order_by'] = order
        else delete query['order_by']
        if (
            search &&
            (!searchSettings['search_type'] ||
                searchSettings['search_type']['value'] !== search)
        )
            query['search_type'] = search
        else delete query['search_type']
        if (exact) query['exact_search'] = exact
        else delete query['exact_search']
        if (offer_type && offer_type !== 'sales')
            query['offer_type'] = offer_type
        else delete query['offer_type']
        if (min_price) query['min_price'] = min_price
        else delete query['min_price']
        if (pfps_only) query['pfps_only'] = pfps_only
        else delete query['pfps_only']
        if (unlisted_only) query['unlisted_only'] = unlisted_only
        else delete query['unlisted_only']
        if (max_price)
            query['max_price'] =
                min_price && parseFloat(max_price) < parseFloat(min_price)
                    ? min_price
                    : max_price
        if (min_mint) query['min_mint'] = min_mint
        else delete query['min_mint']
        if (max_mint)
            query['max_mint'] =
                min_mint && parseInt(max_mint) < parseInt(min_mint)
                    ? min_mint
                    : max_mint
        else delete query['max_mint']
        if (tab) query['tab'] = tab
        else delete query['tab']
        if (verified !== 'verified') query['verified'] = verified
        else delete query['verified']
        if (favorites) query['favorites'] = 'true'
        else delete query['favorites']
        if (backed) query['backed'] = 'true'
        else delete query['backed']
        if (recent) query['recent'] = recent
        else delete query['recent']
        if (offset) query['offset'] = offset
        else delete query['offset']
        if (contract && contract !== 'all') query['contract'] = contract
        else delete query['contract']
        if (attributes) query['attributes'] = attributes
        else delete query['attributes']
        if (limit !== searchSettings['limit']['value']) query['limit'] = limit
        else delete query['limit']
        let newOrder = order
        if (search !== searchVars.searchSearch) {
            newOrder = getOrder(search, order)
            query['order_by'] = newOrder
        }
        const searchStr = qs.stringify(query)
        let searchCount = searchVars.searchCount
        if (searchStr !== searchVars.searchString) {
            searchCount = searchCount + 1
        }

        setSearchVars({
            orderBy: newOrder,
            searchOwner: owner,
            searchCollection: collection,
            searchLimit: limit,
            searchSearch: search,
            searchSchema: schema,
            searchCategory: category,
            searchRecentlySold: recent,
            exactSearch: exact,
            minMint: min_mint,
            maxMint: max_mint,
            minPrice: min_price,
            maxPrice: max_price,
            searchTerm: term,
            searchOffset: offset,
            searchContract: contract,
            searchFavorites: favorites,
            searchBacked: backed,
            searchVerified: verified,
            searchAttributes: attributes,
            searchPFPs: pfps_only,
            searchUnlisted: unlisted_only,
            searchString: searchStr,
            searchCount: searchCount,
        })
        setSearchPathCounter(searchStr)
        setCleared(false)

        return query
    }

    const setVarsAndSearch = (newVars, initial) => {
        setVars(newVars, initial)
        runSearch(newVars, initial)
    }

    const setVarsAndPush = (newVars) => {
        const query = setVars(newVars)
        setQueryStringWithoutPageReloadPush(qs.stringify(query))
    }

    const clearFilters = async () => {
        setVarsAndPush(getClearedVars())

        setOffset(0)
        initScrollManager(false, true, true)

        setCleared(true)
    }

    const changeSearch = async (newVars, initial = false) => {
        if (!newVars['limit'])
            newVars['limit'] = searchSettings['limit']
                ? searchSettings['limit']['value']
                : 40
        if (tradeSearch) {
            setVarsAndSearch(newVars, initial)
        } else {
            setVarsAndPush(newVars, initial)
        }
        setOffset(0)
        initScrollManager(false, true, !tradeSearch)
    }

    const setOrder = async (order) => {
        changeSearch({
            offset: 0,
            order: order,
        })
    }

    const setTerm = async (term) => {
        changeSearch({
            offset: 0,
            term: term,
        })
    }

    const applyTerm = async (term) => {
        changeSearch({
            offset: 0,
            term: term,
        })
    }

    const refreshSearchResults = async () =>
        checkLastSearch(state.last_search_result.path, false)

    const loadMore = async () => {
        if (
            !isLoading &&
            searchResults &&
            searchResults.length > 0 &&
            searchResults.length % searchVars.searchLimit === 0
        ) {
            setIsLoading(true)
            setOffset(offset + searchVars.searchLimit)
        }
    }

    const loadMoreButton = (
        <MainButton className={'mx-auto'} onClick={loadMore}>
            {t('search.load_more')}
        </MainButton>
    )

    return (
        <div
            className={cn(
                { 'flex flex-wrap w-full mt-4': !tradeSearch && !hideFilters },
                { block: tradeSearch },
                { 'flex justify-center w-full': hideFilters },
            )}
            id={'AssetSearch'}
        >
            <div
                className={cn(
                    {
                        'w-full ': !tradeSearch && show && !hideFilters,
                    },
                    {
                        'w-full  h-full':
                            !tradeSearch && (!show || hideFilters),
                    },
                    {
                        'w-full h-auto': tradeSearch,
                    },
                )}
            >
                {!hideFilters ? (
                    <div className={'flex flex-wrap md:h-auto mx-0 mb-auto'}>
                        {searchVars.searchSearch !== 'ft_packs' && (
                            <div
                                className={cn(
                                    'relative flex flex-wrap h-auto',
                                    'w-full my-auto md:mb-auto',
                                    {
                                        'md:mt-0':
                                            searchVars.searchSearch !==
                                            'tradable',
                                    },
                                )}
                            >
                                <TopFilters
                                    disabled={
                                        isPackSearch || isLoading
                                            ? 'disabled'
                                            : ''
                                    }
                                    setOrder={setOrder}
                                    searchTerm={searchVars.searchTerm}
                                    searchSearch={searchVars.searchSearch}
                                    orderBy={searchVars.orderBy}
                                    collectionSchemas={collectionSchemas}
                                    searchCollection={
                                        searchVars.searchCollection
                                    }
                                    searchSchema={searchVars.searchSchema}
                                    setTerm={setTerm}
                                    applyTerm={applyTerm}
                                    refreshSearchResults={refreshSearchResults}
                                    cleared={cleared}
                                    tab={tab}
                                    isLoading={isLoading}
                                    searchResults={searchResults}
                                    searchOffset={offset}
                                    tradeSearch={tradeSearch}
                                    onOpen={onOpen}
                                >
                                    <DrawerLeft
                                        searchSettings={searchSettings}
                                        limit={40}
                                        targetTab={targetTab}
                                        page={page}
                                        searchSchema={searchVars.searchSchema}
                                        searchCategory={
                                            searchVars.searchCategory
                                        }
                                        searchVariant={searchVars.searchVariant}
                                        searchRarity={searchVars.searchRarity}
                                        searchColor={searchVars.searchColor}
                                        searchBorder={searchVars.searchBorder}
                                        searchType={searchVars.searchType}
                                        searchFavorites={
                                            searchVars.searchFavorites
                                        }
                                        searchBacked={searchVars.searchBacked}
                                        searchPFPs={searchVars.searchPFPs}
                                        searchUnlisted={
                                            searchVars.searchUnlisted
                                        }
                                        minPrice={searchVars.minPrice}
                                        maxPrice={searchVars.maxPrice}
                                        minMint={searchVars.minMint}
                                        maxMint={searchVars.maxMint}
                                        setSearchOwner={() => {}}
                                        searchOwner={searchVars.searchOwner}
                                        searchContract={
                                            searchVars.searchContract
                                        }
                                        searchTerm={searchVars.searchTerm}
                                        searchCollection={
                                            searchVars.searchCollection
                                        }
                                        searchSearch={searchVars.searchSearch}
                                        attributes={searchVars.searchAttributes}
                                        setVars={changeSearch}
                                        exactSearch={searchVars.exactSearch}
                                        orderBy={searchVars.orderBy}
                                        searchOfferType={searchOfferType}
                                        isLoading={isLoading}
                                        tradeSearch={tradeSearch}
                                        searchOffset={offset}
                                        currentSearchPath={currentSearchPath}
                                        searchVerified={
                                            searchVars.searchVerified
                                        }
                                        searchLimit={searchVars.searchLimit}
                                        ownerTerm={ownerTerm}
                                        setMaxPrice={() => {}}
                                        setMinPrice={() => {}}
                                        setMinMint={() => {}}
                                        setMaxMint={() => {}}
                                        collectionSchemas={collectionSchemas}
                                        collectionAttributes={
                                            collectionAttributes
                                        }
                                        clearFilters={clearFilters}
                                        searchRecentlySold={
                                            searchVars.searchRecentlySold
                                        }
                                        bulk={props['bulk']}
                                        cleared={cleared}
                                        displayCollectionSelector={
                                            displayCollectionSelector
                                        }
                                        show={show}
                                        setShow={setShow}
                                    />
                                </TopFilters>
                            </div>
                        )}
                    </div>
                ) : (
                    ''
                )}
                <SearchResults
                    {...props}
                    onAdd={onAdd}
                    selectedAssets={selectedAssets}
                    searchDate={searchDate}
                    search={searchVars.searchSearch}
                    name="SearchResults"
                    assets={searchResults.length > 0 ? searchResults : []}
                    onSale={true}
                    page={page}
                    isLoading={isLoading}
                    type={getValue('preview_type', null)}
                    loadMoreButton={tradeSearch ? loadMoreButton : null}
                    limit={searchVars.searchLimit}
                    searchCollection={searchVars.searchCollection}
                    searchOwner={searchVars.searchOwner}
                    searchSearch={searchVars.searchSearch}
                    offset={offset}
                    showOwners={props['showOwners']}
                    selectable={(x) =>
                        [
                            'missing',
                            'cheapest',
                            'cheapest_missing',
                            'sales',
                        ].includes(searchVars.searchSearch)
                            ? x.offer
                            : tab === 'inventory' &&
                              searchVars.searchOwner === userName &&
                              !x.offer &&
                              !x.forSale
                    }
                />
            </div>
        </div>
    )
}

export default SearchComponent
